'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const USER_TYPES = [
  { value: 'farmer', label: 'Farmer / Producer' },
  { value: 'livestock_dealer', label: 'Livestock Dealer' },
  { value: 'haulier', label: 'Haulier / Transporter' },
  { value: 'auctioneer', label: 'Auctioneer' },
  { value: 'machinery_dealer', label: 'Machinery Dealer' },
  { value: 'buyer', label: 'Buyer / Processor' },
  { value: 'other', label: 'Other' },
];

type FormState = 'idle' | 'submitting' | 'success' | 'error' | 'opted_out';

export default function MarketReportsSignup() {
  const { user, profile } = useAuth();
  const supabase = createClient();

  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [optedIn, setOptedIn] = useState(true);

  const [form, setForm] = useState({
    full_name: profile?.full_name ?? '',
    email: user?.email ?? '',
    telephone: '',
    user_type: 'farmer',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    setErrorMsg('');

    try {
      const payload: Record<string, unknown> = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        telephone: form.telephone.trim(),
        user_type: form.user_type,
        opted_in: optedIn,
      };

      if (user?.id) {
        payload.user_id = user.id;
      }

      const { error } = await supabase
        .from('market_report_signups')
        .upsert(payload, { onConflict: 'email' });

      if (error) throw error;

      setFormState(optedIn ? 'success' : 'opted_out');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setErrorMsg(msg);
      setFormState('error');
    }
  };

  if (formState === 'success') {
    return (
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 py-14 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 bg-green-400/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Icon name="CheckCircleIcon" size={36} className="text-green-300" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">You&apos;re signed up!</h3>
          <p className="text-green-200 text-sm leading-relaxed">
            Market reports will be sent to <span className="font-semibold text-white">{form.email}</span>. You can opt out at any time by contacting us or updating your preferences.
          </p>
        </div>
      </section>
    );
  }

  if (formState === 'opted_out') {
    return (
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 py-14 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 bg-amber-400/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Icon name="BellSlashIcon" size={36} className="text-amber-300" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Preferences saved</h3>
          <p className="text-green-200 text-sm">
            You&apos;ve been opted out of market reports. You can opt back in at any time.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 py-14 px-4">
      <div className="max-w-screen-lg mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-16">
          {/* Left: copy */}
          <div className="lg:w-2/5 mb-8 lg:mb-0">
            <div className="inline-flex items-center gap-2 bg-green-700/50 border border-green-600/40 rounded-full px-3 py-1 mb-4">
              <Icon name="ChartBarIcon" size={14} className="text-green-300" />
              <span className="text-green-200 text-xs font-semibold tracking-wide uppercase">Free Market Reports</span>
            </div>
            <h2 className="text-3xl font-extrabold text-white leading-tight mb-4">
              Stay ahead of the market — free weekly reports
            </h2>
            <p className="text-green-200 text-sm leading-relaxed mb-6">
              Get livestock price trends, regional auction results, machinery valuations and deadstock market data delivered straight to your inbox — available to everyone, member or not.
            </p>
            <ul className="flex flex-col gap-3">
              {[
                { icon: 'TagIcon', text: 'Weekly livestock price averages by breed & region' },
                { icon: 'WrenchScrewdriverIcon', text: 'Machinery market valuations & sale results' },
                { icon: 'TruckIcon', text: 'Haulage rate benchmarks across the UK' },
                { icon: 'ArchiveBoxIcon', text: 'Deadstock & wool clip market updates' },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-md bg-green-700/60 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon name={item.icon} size={14} className="text-green-300" />
                  </span>
                  <span className="text-green-100 text-sm">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:w-3/5">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 lg:p-8">
              <h3 className="text-lg font-bold text-white mb-1">Sign up for market reports</h3>
              <p className="text-green-300 text-xs mb-6">Open to all — no membership required</p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-green-200 text-xs font-semibold mb-1.5" htmlFor="mr-full-name">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="mr-full-name"
                      name="full_name"
                      type="text"
                      required
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2.5 text-white placeholder-green-400/60 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-green-200 text-xs font-semibold mb-1.5" htmlFor="mr-email">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="mr-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2.5 text-white placeholder-green-400/60 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 transition-all"
                    />
                  </div>
                </div>

                {/* Telephone + User Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-green-200 text-xs font-semibold mb-1.5" htmlFor="mr-telephone">
                      Telephone <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="mr-telephone"
                      name="telephone"
                      type="tel"
                      required
                      value={form.telephone}
                      onChange={handleChange}
                      placeholder="07700 900000"
                      className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2.5 text-white placeholder-green-400/60 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-green-200 text-xs font-semibold mb-1.5" htmlFor="mr-user-type">
                      I am a… <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="mr-user-type"
                      name="user_type"
                      required
                      value={form.user_type}
                      onChange={handleChange}
                      className="w-full bg-green-900/80 border border-white/25 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400/50 transition-all appearance-none"
                    >
                      {USER_TYPES.map((t) => (
                        <option key={t.value} value={t.value} className="bg-green-900 text-white">
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Opt-in / Opt-out toggle */}
                <div className="bg-white/5 border border-white/15 rounded-xl p-4 flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => setOptedIn((v) => !v)}
                    className={`relative shrink-0 w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-400/50 ${
                      optedIn ? 'bg-green-500' : 'bg-white/20'
                    }`}
                    aria-pressed={optedIn}
                    aria-label="Toggle market report opt-in"
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        optedIn ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <div>
                    <p className="text-white text-sm font-semibold leading-tight">
                      {optedIn ? 'Opted in to market reports' : 'Opted out of market reports'}
                    </p>
                    <p className="text-green-300 text-xs mt-0.5">
                      {optedIn
                        ? 'You will receive weekly market reports by email. You can opt out at any time.' :'You will not receive market reports. Toggle on to opt back in.'}
                    </p>
                  </div>
                </div>

                {/* Error */}
                {formState === 'error' && (
                  <div className="flex items-center gap-2 bg-red-500/20 border border-red-400/30 rounded-lg px-3 py-2.5">
                    <Icon name="ExclamationCircleIcon" size={16} className="text-red-300 shrink-0" />
                    <p className="text-red-200 text-xs">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full bg-green-500 hover:bg-green-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl text-sm transition-colors duration-150 flex items-center justify-center gap-2"
                >
                  {formState === 'submitting' ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Saving…
                    </>
                  ) : (
                    <>
                      <Icon name="EnvelopeIcon" size={16} />
                      {optedIn ? 'Sign me up for market reports' : 'Save my preferences'}
                    </>
                  )}
                </button>

                <p className="text-green-400/70 text-xs text-center">
                  Your details are used solely for market report delivery. We never share your data with third parties.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
